import { baseCompile } from '@intlify/message-compiler'

export type InvalidLocalizationReporter = (
  key: string,
  error: unknown,
) => void

export interface LocalizationValidationOptions {
  referenceMessages?: Record<string, unknown>
  onInvalid?: InvalidLocalizationReporter
}

const NAMED_INTERPOLATION_NODE = 4
const LIST_INTERPOLATION_NODE = 5

function reportInvalidLocalization(key: string, error: unknown) {
  console.warn(`[i18n] Ignoring invalid UI localization "${key}"`, error)
}

function collectInterpolationTokens(value: unknown, tokens: Set<string>) {
  if (Array.isArray(value)) {
    for (const item of value)
      collectInterpolationTokens(item, tokens)
    return
  }

  if (typeof value !== 'object' || value === null)
    return

  const node = value as Record<string, unknown>
  const nodeType = typeof node.type === 'number' ? node.type : node.t
  if (
    nodeType === NAMED_INTERPOLATION_NODE
    && (typeof node.key === 'string' || typeof node.k === 'string')
  ) {
    tokens.add(`{${node.key ?? node.k}}`)
  }
  else if (
    nodeType === LIST_INTERPOLATION_NODE
    && (typeof node.index === 'number' || typeof node.i === 'number')
  ) {
    tokens.add(`{${node.index ?? node.i}}`)
  }

  if (nodeType === 6) {
    const linkedKey = node.key ?? node.k
    const modifier = node.modifier ?? node.m
    const readValue = (value: unknown) => {
      const item = value as { value?: string, v?: string } | undefined
      return item?.value ?? item?.v ?? ''
    }
    tokens.add(`linked:${readValue(linkedKey)}:${readValue(modifier)}`)
  }

  for (const [key, child] of Object.entries(node)) {
    // Source locations contain no interpolation nodes and are relatively large.
    if (key !== 'loc')
      collectInterpolationTokens(child, tokens)
  }
}

function compileInterpolationTokens(message: string): Set<string> {
  const result = baseCompile(message, {
    onError: (error) => {
      throw error
    },
  })
  const tokens = new Set<string>()
  collectInterpolationTokens(result.ast, tokens)
  return tokens
}

function referenceInterpolationTokens(value: unknown): Set<string> | undefined {
  if (typeof value === 'string')
    return compileInterpolationTokens(value)

  if (typeof value !== 'object' || value === null)
    return undefined

  // The i18n build plugin stores bundled messages as compact ASTs in
  // production (`t`, `b`, `i`, `k`) and full compiler ASTs in some test/dev
  // paths. Both carry the same numeric node types.
  const node = value as Record<string, unknown>
  if (typeof node.type !== 'number' && typeof node.t !== 'number')
    return undefined

  const tokens = new Set<string>()
  collectInterpolationTokens(node, tokens)
  return tokens
}

function sortedTokens(tokens: Set<string>) {
  return [...tokens].sort()
}

function haveEqualTokens(expected: Set<string>, actual: Set<string>) {
  return expected.size === actual.size
    && [...expected].every(token => actual.has(token))
}

class LocalizationPlaceholderMismatchError extends Error {
  constructor(expected: Set<string>, actual: Set<string>) {
    const expectedTokens = sortedTokens(expected)
    const actualTokens = sortedTokens(actual)
    super(
      'Localization placeholders do not match: '
      + `expected ${expectedTokens.join(', ') || '(none)'}; `
      + `received ${actualTokens.join(', ') || '(none)'}`,
    )
    this.name = 'LocalizationPlaceholderMismatchError'
  }
}

/**
 * Database-managed messages are compiled lazily by vue-i18n. Validate them
 * before merging so malformed syntax or an incompatible placeholder contract
 * cannot break a report render or silently remove interpolated values.
 */
export function filterValidLocalizationMessages(
  messages: Record<string, string>,
  options: LocalizationValidationOptions = {},
): Record<string, string> {
  const {
    referenceMessages = {},
    onInvalid = reportInvalidLocalization,
  } = options

  return Object.fromEntries(
    Object.entries(messages).filter(([key, message]) => {
      try {
        const actualTokens = compileInterpolationTokens(message)
        const referenceMessage = referenceMessages[key]
        const expectedTokens = referenceInterpolationTokens(referenceMessage)
        if (expectedTokens) {
          if (!haveEqualTokens(expectedTokens, actualTokens)) {
            throw new LocalizationPlaceholderMismatchError(
              expectedTokens,
              actualTokens,
            )
          }
        }
        return true
      }
      catch (error) {
        onInvalid(key, error)
        return false
      }
    }),
  )
}

/** Validate editor input against its immutable bundled key and source message. */
export function assertValidUiLocalization(
  key: string,
  value: string,
  referenceMessages: Record<string, string>,
): string {
  if (!Object.hasOwn(referenceMessages, key))
    throw new Error(`Unknown UI translation key: ${key}`)
  const source = referenceMessages[key]!
  const valid = filterValidLocalizationMessages({ [key]: value }, {
    referenceMessages,
    onInvalid: (_key, error) => { throw error },
  })
  if (!Object.hasOwn(valid, key))
    throw new Error(`Invalid UI translation: ${key}`)
  return source
}
